/**
 * Formatters for Vendor Detail Page
 */

/**
 * Format ISO date string to readable format (UTC to avoid timezone issues)
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return 'N/A';
  }
};

type RiskVariant = 'success' | 'warning' | 'danger' | 'default';

interface RiskLevelDisplay {
  label: string;
  variant: RiskVariant;
}

/**
 * Format risk level for display with badge variant (case-insensitive)
 */
export const formatRiskLevel = (risk: string | null | undefined): RiskLevelDisplay => {
  if (!risk) return { label: 'N/A', variant: 'default' };

  const riskMap: Record<string, { label: string; variant: RiskVariant }> = {
    low: { label: 'Low', variant: 'success' },
    medium: { label: 'Medium', variant: 'warning' },
    high: { label: 'High', variant: 'danger' },
  };

  const normalized = risk.toLowerCase();
  const mapped = riskMap[normalized];

  if (mapped) {
    return mapped;
  }

  return {
    label: risk,
    variant: 'default',
  };
};

/**
 * Format employee count with thousands separator
 */
export const formatEmployeeCount = (count: string | null | undefined): string => {
  if (!count) return 'N/A';

  const num = Number.parseInt(count, 10);
  if (Number.isNaN(num)) return count;

  return num.toLocaleString('en-US');
};

interface AssessmentProgress {
  percentage: number;
  label: string;
}

/**
 * Calculate assessment progress percentage and label
 * Handles edge cases: negative steps, zero/negative totals, step > total
 */
export const getAssessmentProgress = (currentStep: number, totalSteps: number): AssessmentProgress => {
  // Handle invalid inputs - reset to 0 of 0
  if (totalSteps <= 0) {
    return { percentage: 0, label: 'Step 0 of 0' };
  }

  // Clamp step to valid range
  const safeStep = Math.max(0, Math.min(currentStep, totalSteps));
  const percentage = Math.round((safeStep / totalSteps) * 100);

  return {
    percentage,
    label: `Step ${safeStep} of ${totalSteps}`,
  };
};

/**
 * Format URL - ensure it has protocol
 */
export const formatUrl = (url: string | null | undefined): string | null => {
  if (!url || url.trim() === '') return null;

  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

/**
 * Truncate text with ellipsis
 * Only adds '...' when maxLength >= 3 and text needs truncation
 */
export const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return '';
  if (maxLength <= 0) return '';
  if (text.length <= maxLength) return text;

  // Only add ellipsis if we have room for at least some text + ...
  if (maxLength < 3) {
    return text.slice(0, maxLength);
  }

  return `${text.slice(0, maxLength - 3)}...`;
};
